/**
 * ServiceHistoryModal Component
 * Modal for adding or editing service history entries
 */

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../contexts/ToastContext';
import {
  createHistoryEntry,
  updateHistoryEntry,
  getServiceTypes,
  type ServiceHistoryEntry,
  type ServiceType,
} from '../../services/serviceHistoryApi';

interface ServiceHistoryModalProps {
  vehicleId: string;
  entry: ServiceHistoryEntry | null;
  isOpen: boolean;
  onClose: (saved: boolean) => void;
}

export function ServiceHistoryModal({ vehicleId, entry, isOpen, onClose }: ServiceHistoryModalProps) {
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  // Form state
  const [serviceTypeId, setServiceTypeId] = useState('');
  const [performedAt, setPerformedAt] = useState('');
  const [cost, setCost] = useState('');
  const [technicianName, setTechnicianName] = useState('');
  const [notes, setNotes] = useState('');
  const [partsUsed, setPartsUsed] = useState('');

  // Load service types
  useEffect(() => {
    async function loadServiceTypes() {
      try {
        setLoading(true);
        const types = await getServiceTypes();
        setServiceTypes(types.filter((t) => t.isActive));
      } catch {
        toast.error('Error al cargar tipos de servicio');
      } finally {
        setLoading(false);
      }
    }

    if (isOpen) {
      loadServiceTypes();
    }
  }, [isOpen, toast]);

  // Populate form when editing
  useEffect(() => {
    if (entry) {
      setServiceTypeId(entry.serviceTypeId);
      setPerformedAt(format(new Date(entry.performedAt), 'yyyy-MM-dd'));
      setCost(entry.cost ? (entry.cost / 100).toString() : '');
      setTechnicianName(entry.technicianName || '');
      setNotes(entry.notes || '');
      setPartsUsed(entry.partsUsed || '');
    } else {
      // Reset form for new entry
      setServiceTypeId('');
      setPerformedAt(format(new Date(), 'yyyy-MM-dd'));
      setCost('');
      setTechnicianName('');
      setNotes('');
      setPartsUsed('');
    }
  }, [entry, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!serviceTypeId) {
      toast.error('Seleccione un tipo de servicio');
      return;
    }

    if (!performedAt) {
      toast.error('Seleccione una fecha');
      return;
    }

    try {
      setSubmitting(true);

      const costInCents = cost ? Math.round(parseFloat(cost) * 100) : undefined;

      if (entry) {
        // Update existing
        await updateHistoryEntry(vehicleId, entry.id, {
          serviceTypeId,
          performedAt,
          cost: costInCents || null,
          technicianName: technicianName || null,
          notes: notes || null,
          partsUsed: partsUsed || null,
        });
        toast.success('Registro actualizado correctamente');
      } else {
        // Create new
        await createHistoryEntry(vehicleId, {
          serviceTypeId,
          performedAt,
          cost: costInCents,
          technicianName: technicianName || undefined,
          notes: notes || undefined,
          partsUsed: partsUsed || undefined,
        });
        toast.success('Registro creado correctamente');
      }

      onClose(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al guardar registro');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose(false)}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {entry ? 'Editar Registro de Servicio' : 'Nuevo Registro de Servicio'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="serviceType">Tipo de Servicio *</Label>
            <Select
              value={serviceTypeId}
              onValueChange={setServiceTypeId}
              disabled={loading}
            >
              <SelectTrigger id="serviceType">
                <SelectValue placeholder="Seleccionar tipo de servicio" />
              </SelectTrigger>
              <SelectContent>
                {serviceTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="performedAt">Fecha del Servicio *</Label>
            <Input
              id="performedAt"
              type="date"
              value={performedAt}
              onChange={(e) => setPerformedAt(e.target.value)}
              max={format(new Date(), 'yyyy-MM-dd')}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cost">Costo (MXN)</Label>
            <Input
              id="cost"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="technicianName">Nombre del Técnico</Label>
            <Input
              id="technicianName"
              type="text"
              placeholder="Nombre del técnico"
              value={technicianName}
              onChange={(e) => setTechnicianName(e.target.value)}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notas</Label>
            <textarea
              id="notes"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Detalles del servicio realizado..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="partsUsed">Refacciones Utilizadas</Label>
            <textarea
              id="partsUsed"
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Lista de refacciones..."
              value={partsUsed}
              onChange={(e) => setPartsUsed(e.target.value)}
              maxLength={2000}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onClose(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting || loading}>
              {submitting ? 'Guardando...' : entry ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
